import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import Cropper from 'react-easy-crop'
import '../styles/d3/cropImage.scss'
import { getCroppedImg } from 'utils/misc'
import { leftRotationIcon, rightRotationIcon } from 'assets/assets'
import Button from './Button'

const ImageCropper = ({
  img,
  setCroppedImage,
  onSubmit,
  hasSlider,
  isRotative,
  selfButton,
  cameraTip,
  onChevron,
  onDelete
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const showCroppedImage = useCallback(async () => {
    try {
      const croppedImage = await getCroppedImg(img, croppedAreaPixels, rotation)
      setCroppedImage(croppedImage)
    } catch (e) {
      console.error(e)
    }
  }, [croppedAreaPixels])

  const resetImg = () => {
    setZoom(1)
    setRotation(0)
  }

  useEffect(() => {
    if (onSubmit) {
      showCroppedImage()
    }
  }, [onSubmit])

  const rotateImg = isLeft => setRotation(rotation + (isLeft ? 90 : -90))

  return (
    <div className='crop-container'>
      {isRotative && (
        <div className='top-controls flexed space-between-justified'>
          <Button
            className='chevron-left'
            icon='fa fa-chevron-left fa-2x'
            noStyling
            onClick={onChevron}
          />
          <div>
            <Button
              className='rotate-right'
              noStyling
              onClick={() => rotateImg(true)}>
              <img
                className='right-icon'
                src={rightRotationIcon}
                alt='right-icon'
              />
            </Button>
            <Button
              className='rotate-left'
              noStyling
              onClick={() => rotateImg(false)}>
              <img
                className='left-icon'
                src={leftRotationIcon}
                alt='left-icon'
              />
            </Button>
          </div>
          {onDelete ? (
            <Button
              className='delete'
              icon='fas fa-trash-alt fa-2x'
              noStyling
              onClick={onDelete}
            />
          ) : (
            <div />
          )}
        </div>
      )}
      <div className='img-cropper'>
        <Cropper
          image={img}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={1}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          showGrid={false}
        />
      </div>
      {hasSlider && (
        <div className='slider'>
          <input
            type='range'
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            aria-labelledby='Zoom'
            onChange={e => {
              setZoom(e.target.value)
            }}
            className='zoom-range'
          />
          <span>x{Number.parseFloat(zoom).toFixed(1)}</span>
        </div>
      )}
      {cameraTip?.length > 0 && <p className='camera-tip'>{cameraTip}</p>}
      <div className='bottom-controls flexed'>
        <Button
          content='Reset'
          icon='fa fa-repeat'
          noStyling
          onClick={resetImg}
        />
        {selfButton && (
          <Button
            className='done-editing'
            content='Done'
            onClick={showCroppedImage}
          />
        )}
      </div>
    </div>
  )
}

ImageCropper.propTypes = {
  img: PropTypes.string,
  setCroppedImage: PropTypes.func,
  onSubmit: PropTypes.bool,
  hasSlider: PropTypes.bool,
  isRotative: PropTypes.bool,
  selfButton: PropTypes.bool,
  cameraTip: PropTypes.string,
  onChevron: PropTypes.func,
  onDelete: PropTypes.func
}

export default ImageCropper
